import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListSubheader,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import type { CategoryId } from '@timelog/pm-shared';
import { getCategory, isMovableCategory } from '@timelog/pm-shared';
import { useState } from 'react';
import type { MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { useFinancials } from './editor';

import { captureEvent } from '@/analytics/posthogClient';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import { CONTRACT_TYPE_STYLES } from '@/components/planning/ContractChip';
import { MICRO_LABEL_TRACKING } from '@/components/ui';

/**
 * The card kebab: "Bill via <sibling>" (BR-10, movable categories only),
 * "Use contract default" to take a moved category back, and "Hide from this
 * contract" (BR-9 — a non-zero amount asks before zeroing).
 */
export const CategoryKebab = ({ categoryId }: { categoryId: CategoryId }) => {
  const { state, dispatch, effectiveContracts, actions } = useFinancials();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [confirmHide, setConfirmHide] = useState(false);
  const { t } = useTranslation(['pmFinancials', 'pmCommon']);

  const category = getCategory(state.document, categoryId);
  const siblings = effectiveContracts.filter((contract) => contract.id !== state.contractId);
  const movable = isMovableCategory(categoryId);

  const open = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchor(event.currentTarget);
  };

  const hide = () => {
    setAnchor(null);
    if (category.amount > 0) {
      setConfirmHide(true);
      return;
    }
    dispatch({ type: 'SET_CATEGORY_HIDDEN', id: categoryId, hidden: true });
    captureEvent('category_removed', { category: categoryId });
  };

  return (
    <>
      <Box
        component="button"
        type="button"
        data-automation-id="category-kebab"
        onClick={open}
        sx={{
          width: 20,
          height: 20,
          flex: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          bgcolor: 'transparent',
          borderRadius: 'var(--border-radius-small)',
          color: 'var(--fg-3)',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'var(--bg-hover)', color: 'var(--fg-2)' },
        }}
      >
        <MaterialSymbol name="more_vert" size={15} />
      </Box>

      <Menu anchorEl={anchor} open={anchor !== null} onClose={() => setAnchor(null)}>
        {movable && !category.billedVia && siblings.length > 0
          ? [
              <ListSubheader
                key="header"
                sx={{
                  fontSize: 10,
                  letterSpacing: MICRO_LABEL_TRACKING,
                  textTransform: 'uppercase',
                  lineHeight: '26px',
                }}
              >
                {t('categoryKebab.BillUsingMethod')}
              </ListSubheader>,
              ...siblings.map((sibling) => {
                const style = CONTRACT_TYPE_STYLES[sibling.type];
                return (
                  <MenuItem
                    key={sibling.id}
                    data-automation-id="category-move-target"
                    onClick={() => {
                      setAnchor(null);
                      if (isMovableCategory(categoryId)) {
                        void actions.moveCategory(categoryId, sibling.id, 'out');
                      }
                    }}
                  >
                    <Box
                      component="span"
                      sx={{
                        width: 17,
                        height: 17,
                        mr: 1,
                        flex: 'none',
                        borderRadius: 'var(--border-radius-small)',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: 10,
                        fontWeight: 600,
                        fontFamily: 'var(--font-family-mono)',
                        bgcolor: style.badgeBg,
                        color: style.badgeFg,
                      }}
                    >
                      {t(`pmCommon:billingLetter.${sibling.type}`)}
                    </Box>
                    {sibling.name}
                  </MenuItem>
                );
              }),
              <Divider key="divider" />,
            ]
          : null}
        {movable && category.billedVia ? (
          <MenuItem
            data-automation-id="category-move-back"
            onClick={() => {
              const target = category.billedVia;
              setAnchor(null);
              if (target && isMovableCategory(categoryId)) {
                void actions.moveCategory(categoryId, target, 'back');
              }
            }}
          >
            <MaterialSymbol name="undo" size={15} color="var(--fg-3)" />
            <Box component="span" sx={{ ml: 1 }}>
              {t('categoryKebab.UseContractDefault')}
            </Box>
          </MenuItem>
        ) : null}
        {!category.billedVia ? (
          <MenuItem data-automation-id="category-hide" onClick={hide}>
            <MaterialSymbol name="visibility_off" size={15} color="var(--fg-3)" />
            <Box component="span" sx={{ ml: 1 }}>
              {t('categoryKebab.Hide')}
            </Box>
          </MenuItem>
        ) : null}
      </Menu>

      <Dialog
        open={confirmHide}
        onClose={() => setConfirmHide(false)}
        maxWidth="xs"
        fullWidth
        data-automation-id="hide-category-dialog"
      >
        <DialogTitle>
          {t('categoryKebab.HideTitle', { category: t(`pmCommon:category.${categoryId}`) })}
        </DialogTitle>
        <DialogContent>
          <Typography variant="bodyRegular" color="text.secondary">
            {t('categoryKebab.HideBody')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmHide(false)} color="inherit">
            {t('actions.Cancel')}
          </Button>
          <Button
            variant="contained"
            data-automation-id="hide-category-confirm"
            onClick={() => {
              setConfirmHide(false);
              dispatch({ type: 'SET_CATEGORY_HIDDEN', id: categoryId, hidden: true });
              captureEvent('category_removed', { category: categoryId });
            }}
          >
            {t('categoryKebab.ZeroAndHide')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
